import React from 'react'
import { useOutletContext } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormHelperText,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/AddRounded'
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded'
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded'
import EditIcon from '@mui/icons-material/EditRounded'
import DragIndicatorIcon from '@mui/icons-material/DragIndicatorRounded'
import SearchIcon from '@mui/icons-material/SearchRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import AccessTimeIcon from '@mui/icons-material/AccessTimeRounded'
import MoreVertIcon from '@mui/icons-material/MoreVertRounded'
import TypeChip, { TypeChipProvider } from '../../components/showClock/TypeChip'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { MeResponse } from '../../api/me'
import {
  createClockTemplate,
  deleteClockTemplate,
  duplicateClockTemplate,
  getClockMeta,
  getClockTemplate,
  listClockTemplates,
  updateClockTemplate,
  type ClockTemplateDetail,
  type ClockTemplateItem,
  type ClockTemplateItemInput,
  type ClockTemplateSummary,
  type ClockMetaResponse,
} from '../../api/showClock'
import { usePageTitle } from '../../components/common/PageTitleContext'
import useStationStore from '../../store/station'
import useUiNavStore from '../../state/uiNavStore'
import {
  FALLBACK_CLOCK_ITEM_TYPES,
  formatClockDuration,
  formatClockRelativeDate,
  resolveClockItemTypes,
  type ClockItemTypeMeta,
} from '../../utils/showClock'
import { useDisplayTitles } from '../../utils/clockDisplayTitle'

type OutletContext = { me: MeResponse; includeSuperadmin: boolean }

type TemplateEditorMode = 'create' | 'edit'

interface TemplateItemDraft {
  key: string
  id?: number
  type: string
  title: string | null
  durationMinutes: number
  durationSecondsPart: number
  durationSeconds: number
  autoAdvance: boolean
  note: string
  isFixedTime: boolean
  fixedTimeMinute: number | null
  fixedTimeSecond: number | null
}

interface TemplateDraft {
  id?: number
  name: string
  notes: string
  items: TemplateItemDraft[]
}

interface TemplateItemErrors {
  type?: string
  title?: string
  duration?: string
  fixedTime?: string
}

interface TemplateFormErrors {
  name?: string
  items?: string
  itemErrors: Record<string, TemplateItemErrors>
  general?: string
}

interface SnackbarState {
  message: string
  severity: 'success' | 'error'
}

const NAV_PINNED_WIDTH = 280
const NAV_COLLAPSED_WIDTH = 80

let tempTemplateIdCounter = 0

const createTempTemplateId = (): number => {
  tempTemplateIdCounter -= 1
  return tempTemplateIdCounter
}

const createDraftItemKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `item-${crypto.randomUUID()}`
  }
  return `item-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const defaultAutoAdvanceForType = (typeKey: string): boolean => {
  const normalized = (typeKey ?? '').toLowerCase()
  return normalized === 'news' || normalized === 'weather' || normalized === 'commercial_break'
}

const clampDurationMinutesInput = (value: number | null | undefined): number => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return 0
  }

  const minutes = Math.floor(value)

  if (!Number.isFinite(minutes)) {
    return 0
  }

  return Math.max(0, Math.min(59, minutes))
}

const clampDurationSecondsInput = (value: number | null | undefined): number => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return 0
  }

  const seconds = Math.floor(value)

  if (!Number.isFinite(seconds)) {
    return 0
  }

  const clamped = Math.max(0, Math.min(59, seconds))
  const snapped = Math.round(clamped / 10) * 10

  if (snapped >= 60) {
    return 50
  }

  return snapped
}

const normalizeDurationComponents = (
  minutesInput: number | null | undefined,
  secondsInput: number | null | undefined,
): { minutes: number; seconds: number } => {
  let minutes = Number.isFinite(minutesInput as number) ? Math.floor((minutesInput as number) ?? 0) : 0
  if (minutes < 0) {
    minutes = 0
  }

  let seconds = Number.isFinite(secondsInput as number) ? Math.round((secondsInput as number) / 10) * 10 : 0

  if (!Number.isFinite(seconds)) {
    seconds = 0
  }

  if (seconds >= 60) {
    const extraMinutes = Math.floor(seconds / 60)
    minutes += extraMinutes
    seconds -= extraMinutes * 60
  }

  if (seconds < 0) {
    const deficitMinutes = Math.ceil(Math.abs(seconds) / 60)
    minutes = Math.max(0, minutes - deficitMinutes)
    seconds += deficitMinutes * 60
  }

  seconds = Math.max(0, Math.min(59, seconds))
  seconds = Math.round(seconds / 10) * 10

  if (seconds === 60) {
    minutes += 1
    seconds = 0
  }

  if (minutes === 0 && seconds < 0) {
    seconds = 0
  }

  return {
    minutes,
    seconds,
  }
}

const decomposeDurationSeconds = (totalSeconds: number | null | undefined): {
  minutes: number
  seconds: number
} => {
  const safeTotal = typeof totalSeconds === 'number' && Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0
  const minutes = Math.floor(safeTotal / 60)
  const seconds = safeTotal % 60
  return normalizeDurationComponents(minutes, seconds)
}

const parseTemplateTimeParts = (
  value: string | null | undefined,
): { hours: number; minutes: number; seconds: number } | null => {
  if (!value) {
    return null
  }

  const trimmed = value.trim()

  if (!trimmed) {
    return null
  }

  const segments = trimmed.split(/[T\s]/)
  let candidate = segments.length > 1 ? segments[segments.length - 1] : trimmed

  candidate = candidate.replace(/Z$/i, '')

  const offsetMatch = candidate.match(/^(.+?)([+-]\d{2}:?\d{2})$/)
  if (offsetMatch) {
    candidate = offsetMatch[1]
  }

  const fractionalIndex = candidate.indexOf('.')
  if (fractionalIndex !== -1) {
    candidate = candidate.slice(0, fractionalIndex)
  }

  const timeSegments = candidate
    .split(':')
    .map((segment) => segment.trim())
    .filter((segment) => segment !== '')

  if (timeSegments.length < 2) {
    return null
  }

  let hours = 0
  let minutes = 0
  let seconds = 0

  if (timeSegments.length === 2) {
    minutes = Number.parseInt(timeSegments[0], 10)
    seconds = Number.parseInt(timeSegments[1], 10)
  } else {
    const normalizedSegments = timeSegments.slice(-3)
    if (normalizedSegments.length === 2) {
      minutes = Number.parseInt(normalizedSegments[0], 10)
      seconds = Number.parseInt(normalizedSegments[1], 10)
    } else {
      hours = Number.parseInt(normalizedSegments[0], 10)
      minutes = Number.parseInt(normalizedSegments[1], 10)
      seconds = Number.parseInt(normalizedSegments[2], 10)
    }
  }

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    Number.isNaN(seconds) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59 ||
    seconds < 0 ||
    seconds > 59
  ) {
    return null
  }

  if (timeSegments.length === 2) {
    hours = 0
  }

  return { hours, minutes, seconds }
}

const normalizeFixedTimeMinute = (value: number | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null
  }

  if (!Number.isFinite(value)) {
    return null
  }

  const minute = Math.floor(value)

  if (!Number.isFinite(minute)) {
    return null
  }

  return Math.max(0, Math.min(59, minute))
}

const normalizeFixedTimeSecond = (value: number | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null
  }

  if (!Number.isFinite(value)) {
    return null
  }

  const seconds = Math.floor(value)

  if (!Number.isFinite(seconds)) {
    return null
  }

  const clamped = Math.max(0, Math.min(59, seconds))
  const snapped = Math.round(clamped / 10) * 10

  if (snapped >= 60) {
    return 50
  }

  return snapped
}

const parseFixedTimeAnchorParts = (
  value: string | null | undefined,
): { minute: number | null; second: number | null } => {
  const parts = parseTemplateTimeParts(value)

  if (!parts) {
    return { minute: null, second: null }
  }

  return {
    minute: normalizeFixedTimeMinute(parts.minutes),
    second: normalizeFixedTimeSecond(parts.seconds),
  }
}

const formatFixedTimeAnchorForPayload = (
  minute: number | null | undefined,
  second: number | null | undefined,
): string | null => {
  const normalizedMinute = normalizeFixedTimeMinute(minute)

  if (normalizedMinute === null) {
    return null
  }

  const normalizedSecond = normalizeFixedTimeSecond(second) ?? 0

  const minuteString = normalizedMinute.toString().padStart(2, '0')
  const secondString = normalizedSecond.toString().padStart(2, '0')

  return `${minuteString}:${secondString}`
}

const sumDuration = (items: ClockTemplateItem[] | undefined): number | null => {
  if (!items?.length) {
    return null
  }
  return items.reduce((total, item) => total + (item.default_duration_seconds ?? 0), 0)
}

const templateDetailToSummary = (detail: ClockTemplateDetail): ClockTemplateSummary => ({
  id: detail.id,
  station_id: detail.station_id,
  name: detail.name,
  notes: detail.notes ?? null,
  item_count: detail.item_count,
  estimated_duration_seconds:
    detail.estimated_duration_seconds ?? sumDuration(detail.items) ?? detail.estimated_duration_seconds ?? null,
  last_used_at: detail.last_used_at ?? null,
  updated_at: detail.updated_at ?? null,
  created_at: detail.created_at ?? null,
})

const buildDraftFromTemplate = (template: ClockTemplateDetail): TemplateDraft => ({
  id: template.id,
  name: template.name,
  notes: template.notes ?? '',
  items: template.items
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((item) => ({
      key: `existing-${item.id}`,
      id: item.id,
      type: item.type,
      title: item.title ? item.title : null,
      ...(() => {
        const { minutes, seconds } = decomposeDurationSeconds(item.default_duration_seconds)
        const totalSeconds = (minutes * 60 + seconds) || 0
        return {
          durationMinutes: minutes,
          durationSecondsPart: seconds,
          durationSeconds: totalSeconds,
        }
      })(),
      autoAdvance: Boolean(item.auto_advance),
      note: item.note ?? '',
      isFixedTime: Boolean(item.is_fixed_time),
      ...(() => {
        const { minute, second } = parseFixedTimeAnchorParts(item.fixed_time_at ?? '')
        return {
          fixedTimeMinute: minute,
          fixedTimeSecond: second,
        }
      })(),
    })),
})

const buildEmptyDraft = (itemTypes: ClockItemTypeMeta[]): TemplateDraft => {
  const fallback = itemTypes[0] ?? FALLBACK_CLOCK_ITEM_TYPES.song
  const defaultItem: TemplateItemDraft = {
    key: createDraftItemKey(),
    type: fallback.key,
    title: null,
    ...(() => {
      const { minutes, seconds } = decomposeDurationSeconds(fallback.defaultDurationSeconds)
      const totalSeconds = (minutes * 60 + seconds) || 0
      return {
        durationMinutes: minutes,
        durationSecondsPart: seconds,
        durationSeconds: totalSeconds,
      }
    })(),
    autoAdvance: defaultAutoAdvanceForType(fallback.key),
    note: '',
    isFixedTime: false,
    fixedTimeMinute: null,
    fixedTimeSecond: null,
  }

  return {
    name: '',
    notes: '',
    items: [defaultItem],
  }
}

const normalizeDraftItem = (item: TemplateItemDraft, position: number): ClockTemplateItemInput => {
  const rawTitle = item.title ?? ''
  const trimmedTitle = rawTitle.trim()
  const title = trimmedTitle === '' ? null : trimmedTitle
  const note = (item.note ?? '').trim()

  return {
    id: item.id,
    type: item.type,
    title,
    default_duration_seconds: Math.max(0, Math.floor(item.durationSeconds)),
    auto_advance: item.autoAdvance,
    note: note ? note : null,
    is_fixed_time: item.isFixedTime,
    fixed_time_at: item.isFixedTime
      ? formatFixedTimeAnchorForPayload(item.fixedTimeMinute, item.fixedTimeSecond)
      : null,
    position,
  }
}

const formatExpectedStartLabel = (expectedStartSeconds: number): string => {
  const safeSeconds = Number.isFinite(expectedStartSeconds)
    ? Math.max(0, Math.floor(expectedStartSeconds))
    : 0
  const hourNum = Math.floor(safeSeconds / 3600) + 1
  const minutes = Math.floor((safeSeconds % 3600) / 60)
  const seconds = safeSeconds % 60

  return `Hour ${hourNum} · ${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`
}

const SortableTemplateItemRow: React.FC<{
  item: TemplateItemDraft
  displayTitle: string
  expectedStartLabel: string
  itemTypes: ClockItemTypeMeta[]
  errors: TemplateItemErrors | undefined
  index: number
  onChange: (key: string, updates: Partial<TemplateItemDraft>) => void
  onToggleFixed: (key: string, value: boolean) => void
  onRemove: (key: string) => void
  onInsertAfter: (index: number) => void
  pendingFocusKey: string | null
  onPendingFocusHandled: (key: string) => void
  disableRemove: boolean
  notesExpanded: boolean
  onToggleNotes: (key: string, expanded: boolean) => void
}> = ({
  item,
  displayTitle,
  expectedStartLabel,
  itemTypes,
  errors,
  index,
  onChange,
  onToggleFixed,
  onRemove,
  onInsertAfter,
  pendingFocusKey,
  onPendingFocusHandled,
  disableRemove,
  notesExpanded,
  onToggleNotes,
}) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: item.key,
  })

  const noteInputRef = React.useRef<HTMLTextAreaElement | HTMLInputElement | null>(null)
  const titleInputRef = React.useRef<HTMLInputElement | null>(null)
  const typeInputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement | null>(null)
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null)

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  const handleInsertAfterClick = () => {
    handleMenuClose()
    onInsertAfter(index)
  }

  const handleRemoveClick = () => {
    handleMenuClose()
    onRemove(item.key)
  }

  React.useEffect(() => {
    if (notesExpanded && noteInputRef.current) {
      const element = noteInputRef.current
      element.focus()
      if ('setSelectionRange' in element) {
        const length = element.value.length
        element.setSelectionRange(length, length)
      }
    }
  }, [notesExpanded])

  React.useEffect(() => {
    if (pendingFocusKey !== item.key) {
      return
    }

    const titleElement = titleInputRef.current
    const typeElement = typeInputRef.current
    const focusTarget = titleElement ?? typeElement ?? null

    if (focusTarget) {
      focusTarget.focus()
      if (
        focusTarget instanceof HTMLInputElement ||
        focusTarget instanceof HTMLTextAreaElement
      ) {
        const length = focusTarget.value.length
        focusTarget.setSelectionRange(length, length)
      }
    }

    onPendingFocusHandled(item.key)
  }, [item.key, onPendingFocusHandled, pendingFocusKey])

  const hasNote = Boolean(item.note.trim())

  const handleMinutesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawMinutes = Number(event.target.value)
    const sanitizedMinutes = clampDurationMinutesInput(rawMinutes)
    const secondsForNormalization = clampDurationSecondsInput(item.durationSecondsPart)
    const { minutes: normalizedMinutes, seconds } = normalizeDurationComponents(
      sanitizedMinutes,
      secondsForNormalization,
    )
    const nextMinutes = clampDurationMinutesInput(normalizedMinutes)
    const totalSeconds = nextMinutes * 60 + seconds
    onChange(item.key, {
      durationMinutes: nextMinutes,
      durationSecondsPart: seconds,
      durationSeconds: totalSeconds,
    })
  }

  const handleSecondsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawSeconds = Number(event.target.value)
    const sanitizedSeconds = clampDurationSecondsInput(rawSeconds)
    const baseMinutes = clampDurationMinutesInput(item.durationMinutes)
    const { minutes, seconds } = normalizeDurationComponents(baseMinutes, sanitizedSeconds)
    const nextMinutes = clampDurationMinutesInput(minutes)
    const totalSeconds = nextMinutes * 60 + seconds
    onChange(item.key, {
      durationMinutes: nextMinutes,
      durationSecondsPart: seconds,
      durationSeconds: totalSeconds,
    })
  }

  const handleFixedMinuteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    if (value === '') {
      onChange(item.key, { fixedTimeMinute: null })
      return
    }

    const rawValue = Number(value)
    const minute = normalizeFixedTimeMinute(rawValue)
    onChange(item.key, { fixedTimeMinute: minute })
  }

  const handleFixedSecondChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    if (value === '') {
      onChange(item.key, { fixedTimeSecond: null })
      return
    }

    const rawValue = Number(value)
    const second = normalizeFixedTimeSecond(rawValue)
    onChange(item.key, { fixedTimeSecond: second })
  }

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  }

  const displayIndex = index + 1
  const menuOpen = Boolean(menuAnchorEl)

  return (
    <Paper ref={setNodeRef} style={style} sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <IconButton
          size="small"
          color="inherit"
          {...attributes}
          {...listeners}
          ref={setActivatorNodeRef}
          aria-label="Drag to reorder"
        >
          <DragIndicatorIcon />
        </IconButton>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.75, sm: 2 }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ flexGrow: 1, minWidth: 0 }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 0, flexGrow: 1, flexBasis: 0 }}
          >
            <Chip
              label={displayIndex}
              size="small"
              color="default"
              sx={{
                fontWeight: 600,
                borderRadius: '999px',
                height: 24,
                px: 1,
              }}
            />
            <TypeChip
              typeKey={item.type}
              label={displayTitle}
              size="large"
              variant="filled"
              sx={{
                minWidth: 0,
                flexGrow: 1,
                maxWidth: '100%',
                '& .MuiChip-label': {
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  textAlign: 'left',
                },
              }}
            />
          </Stack>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontWeight: 500,
              textAlign: { xs: 'left', sm: 'right' },
              minWidth: { sm: 160 },
              width: { xs: '100%', sm: 'auto' },
              flexShrink: 0,
              ml: { sm: 'auto' },
            }}
          >
            {expectedStartLabel}
          </Typography>
        </Stack>
        <IconButton
          size="small"
          color="inherit"
          aria-label="Item actions"
          aria-haspopup="true"
          aria-expanded={menuOpen ? 'true' : undefined}
          onClick={handleMenuOpen}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={handleInsertAfterClick}>Insert item after this</MenuItem>
          <MenuItem onClick={handleRemoveClick} disabled={disableRemove}>
            Delete item
          </MenuItem>
        </Menu>
      </Stack>
      <Grid container spacing={2} columns={{ xs: 1, sm: 8, md: 12 }}>
        <Grid item xs={1} sm={4} md={4}>
          <TextField
            select
            label="Type"
            value={item.type}
            onChange={(event) => onChange(item.key, { type: event.target.value })}
            fullWidth
            error={Boolean(errors?.type)}
            helperText={errors?.type ?? 'Choose the item category.'}
            SelectProps={{ inputRef: typeInputRef }}
          >
            {itemTypes.map((type) => (
              <MenuItem key={type.key} value={type.key}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={1} sm={4} md={4}>
          <TextField
            label="Title"
            value={item.title ?? ''}
            onChange={(event) => onChange(item.key, { title: event.target.value })}
            fullWidth
            error={Boolean(errors?.title)}
            helperText={
              errors?.title ?? 'Optional custom label displayed in daily clocks & live view.'
            }
            inputRef={titleInputRef}
          />
        </Grid>
        <Grid item xs={1} sm={4} md={4}>
          <Stack spacing={0.75} sx={{ width: '100%' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <TextField
                type="number"
                label="Minutes"
                value={item.durationMinutes}
                onChange={handleMinutesChange}
                fullWidth
                inputProps={{ min: 0, max: 59, step: 1 }}
                sx={{
                  flex: { xs: 1, sm: '0 0 140px' },
                  maxWidth: { xs: '100%', sm: 140 },
                  minWidth: { sm: 120 },
                }}
              />
              <TextField
                type="number"
                label="Seconds"
                value={item.durationSecondsPart}
                onChange={handleSecondsChange}
                fullWidth
                inputProps={{ min: 0, max: 59, step: 10 }}
                sx={{
                  flex: { xs: 1, sm: '0 0 140px' },
                  maxWidth: { xs: '100%', sm: 140 },
                  minWidth: { sm: 120 },
                }}
              />
            </Stack>
            <FormHelperText error={Boolean(errors?.duration)}>
              {errors?.duration ?? 'Minutes × 60 + seconds, seconds step 10.'}
            </FormHelperText>
          </Stack>
        </Grid>
        <Grid item xs={1} sm={8} md={12}>
          <Stack spacing={notesExpanded ? 1.5 : 1} sx={{ width: '100%' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
            >
              <Button
                startIcon={notesExpanded ? <CloseIcon fontSize="small" /> : <AddIcon />}
                onClick={() => onToggleNotes(item.key, !notesExpanded)}
                size={notesExpanded ? 'small' : 'medium'}
                sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
              >
                {notesExpanded ? 'Hide notes' : hasNote ? 'Show notes' : 'Add notes'}
              </Button>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={item.autoAdvance}
                        onChange={(event) => onChange(item.key, { autoAdvance: event.target.checked })}
                      />
                    }
                    label="Auto advance"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={item.isFixedTime}
                        onChange={(event) => onToggleFixed(item.key, event.target.checked)}
                      />
                    }
                    label="Fixed time anchor"
                  />
                {item.isFixedTime ? (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <TextField
                      type="number"
                      label="Minute"
                      value={item.fixedTimeMinute ?? ''}
                      onChange={handleFixedMinuteChange}
                      fullWidth
                      inputProps={{ min: 0, max: 59, step: 1 }}
                      error={Boolean(errors?.fixedTime)}
                      sx={{
                        flex: { xs: 1, sm: '0 0 120px' },
                        maxWidth: { xs: '100%', sm: 120 },
                        minWidth: { sm: 110 },
                      }}
                    />
                    <TextField
                      type="number"
                      label="Second"
                      value={item.fixedTimeSecond ?? ''}
                      onChange={handleFixedSecondChange}
                      fullWidth
                      inputProps={{ min: 0, max: 59, step: 10 }}
                      error={Boolean(errors?.fixedTime)}
                      sx={{
                        flex: { xs: 1, sm: '0 0 120px' },
                        maxWidth: { xs: '100%', sm: 120 },
                        minWidth: { sm: 110 },
                      }}
                    />
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
            {item.isFixedTime ? (
              <FormHelperText
                error={Boolean(errors?.fixedTime)}
                sx={{
                  alignSelf: { xs: 'stretch', sm: 'flex-end' },
                  textAlign: { sm: 'right' },
                }}
              >
                {errors?.fixedTime ?? 'Anchors are minute of each hour (with optional seconds).'}
              </FormHelperText>
            ) : null}
            {notesExpanded ? (
              <TextField
                label="Notes"
                value={item.note}
                onChange={(event) => onChange(item.key, { note: event.target.value })}
                fullWidth
                multiline
                minRows={2}
                helperText="Optional guidance that travels with the item."
                inputRef={noteInputRef}
              />
            ) : null}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  )
}

const TemplateEditorDrawer: React.FC<{
  open: boolean
  mode: TemplateEditorMode
  draft: TemplateDraft | null
  itemTypes: ClockItemTypeMeta[]
  errors: TemplateFormErrors
  saving: boolean
  loading: boolean
  onClose: () => void
  onDraftChange: (updater: (prev: TemplateDraft) => TemplateDraft) => void
  onSubmit: () => void
}> = ({
  open,
  mode,
  draft,
  itemTypes,
  errors,
  saving,
  loading,
  onClose,
  onDraftChange,
  onSubmit,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const navPinned = useUiNavStore((state) => state.pinned)
  const navWidth = navPinned ? NAV_PINNED_WIDTH : NAV_COLLAPSED_WIDTH

  const [expandedNotesByKey, setExpandedNotesByKey] = React.useState<Record<string, boolean>>({})
  const [pendingFocusKey, setPendingFocusKey] = React.useState<string | null>(null)

  const templateHeaderName = draft?.name?.trim() ? draft.name.trim() : 'Untitled'
  const headerTitle = mode === 'create' ? 'New template' : `EDIT TEMPLATE — ${templateHeaderName}`

  const orderedDraftItems = React.useMemo(
    () =>
      draft?.items.map((item) => ({
        id: item.key,
        item_type: item.type,
        title: item.title,
      })) ?? [],
    [draft],
  )
  const displayTitles = useDisplayTitles(orderedDraftItems)

  React.useEffect(() => {
    if (!draft) {
      setExpandedNotesByKey({})
      return
    }

    setExpandedNotesByKey((prev) => {
      const next: Record<string, boolean> = { ...prev }
      const keys = new Set<string>()

      for (const item of draft.items) {
        keys.add(item.key)
        if (next[item.key] === undefined) {
          next[item.key] = Boolean(item.note.trim())
        }
      }

      for (const key of Object.keys(next)) {
        if (!keys.has(key)) {
          delete next[key]
        }
      }

      return next
    })
  }, [draft])

  React.useEffect(() => {
    if (!draft) {
      setPendingFocusKey(null)
    }
  }, [draft])

  const createDefaultDraftItem = React.useCallback((): TemplateItemDraft => {
    const availableTypes = itemTypes
    const defaultType = availableTypes[0] ?? FALLBACK_CLOCK_ITEM_TYPES.song
    const { minutes, seconds } = decomposeDurationSeconds(defaultType.defaultDurationSeconds)
    const totalSeconds = (minutes * 60 + seconds) || 0

    return {
      key: createDraftItemKey(),
      type: defaultType.key,
      title: null,
      durationMinutes: minutes,
      durationSecondsPart: seconds,
      durationSeconds: totalSeconds,
      autoAdvance: defaultAutoAdvanceForType(defaultType.key),
      note: '',
      isFixedTime: false,
      fixedTimeMinute: null,
      fixedTimeSecond: null,
    }
  }, [itemTypes])

  const handleToggleItemNotes = React.useCallback((key: string, expanded: boolean) => {
    setExpandedNotesByKey((prev) => ({
      ...prev,
      [key]: expanded,
    }))
  }, [])

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      if (!draft) {
        return
      }
      const { active, over } = event
      if (!over || active.id === over.id) {
        return
      }
      onDraftChange((prev) => {
        const currentIndex = prev.items.findIndex((item) => item.key === active.id)
        const targetIndex = prev.items.findIndex((item) => item.key === over.id)
        if (currentIndex === -1 || targetIndex === -1) {
          return prev
        }
        const items = arrayMove(prev.items, currentIndex, targetIndex)
        return { ...prev, items }
      })
    },
    [draft, onDraftChange],
  )

  const handleChangeItem = React.useCallback(
    (key: string, updates: Partial<TemplateItemDraft>) => {
      onDraftChange((prev) => ({
        ...prev,
        items: prev.items.map((item) => {
          if (item.key !== key) {
            return item
          }

          let durationMinutes = item.durationMinutes
          let durationSecondsPart = item.durationSecondsPart
          let durationSeconds = item.durationSeconds
          let type = item.type
          let title = item.title
          let autoAdvance = item.autoAdvance
          let fixedTimeMinute = item.fixedTimeMinute
          let fixedTimeSecond = item.fixedTimeSecond

          if (updates.durationSeconds !== undefined) {
            const normalized = decomposeDurationSeconds(updates.durationSeconds)
            durationMinutes = normalized.minutes
            durationSecondsPart = normalized.seconds
            durationSeconds = durationMinutes * 60 + durationSecondsPart
          }

          if (updates.durationMinutes !== undefined || updates.durationSecondsPart !== undefined) {
            const normalized = normalizeDurationComponents(
              updates.durationMinutes ?? durationMinutes,
              updates.durationSecondsPart ?? durationSecondsPart,
            )
            durationMinutes = normalized.minutes
            durationSecondsPart = normalized.seconds
            durationSeconds = durationMinutes * 60 + durationSecondsPart
          }

          if (updates.type !== undefined) {
            const nextType = updates.type
            const previousMeta = itemTypes.find((entry) => entry.key === item.type)
            const nextMeta = itemTypes.find((entry) => entry.key === nextType)
            type = nextType
            const usingPreviousDefault = previousMeta
              ? item.durationSeconds === previousMeta.defaultDurationSeconds
              : false
            if (
              nextMeta &&
              usingPreviousDefault &&
              updates.durationSeconds === undefined &&
              updates.durationMinutes === undefined &&
              updates.durationSecondsPart === undefined
            ) {
              const normalized = decomposeDurationSeconds(nextMeta.defaultDurationSeconds)
              durationMinutes = normalized.minutes
              durationSecondsPart = normalized.seconds
              durationSeconds = durationMinutes * 60 + durationSecondsPart
            }

            if (updates.autoAdvance === undefined) {
              const previousDefault = defaultAutoAdvanceForType(item.type)
              const nextDefault = defaultAutoAdvanceForType(nextType)
              if (item.autoAdvance === previousDefault) {
                autoAdvance = nextDefault
              }
            }
          }

          if (updates.autoAdvance !== undefined) {
            autoAdvance = updates.autoAdvance
          }

          if (updates.title !== undefined) {
            const nextTitle = updates.title
            if (nextTitle === null) {
              title = null
            } else {
              title = nextTitle.trim() === '' ? null : nextTitle
            }
          }

          if (updates.fixedTimeMinute !== undefined) {
            fixedTimeMinute = normalizeFixedTimeMinute(updates.fixedTimeMinute)
          }

          if (updates.fixedTimeSecond !== undefined) {
            fixedTimeSecond = normalizeFixedTimeSecond(updates.fixedTimeSecond)
          }

          return {
            ...item,
            ...updates,
            title,
            type,
            durationMinutes,
            durationSecondsPart,
            durationSeconds,
            autoAdvance,
            fixedTimeMinute,
            fixedTimeSecond,
          }
        }),
      }))
    },
    [itemTypes, onDraftChange],
  )

  const handleToggleFixed = React.useCallback(
    (key: string, value: boolean) => {
      onDraftChange((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.key === key
            ? {
                ...item,
                isFixedTime: value,
                fixedTimeMinute: value ? item.fixedTimeMinute : null,
                fixedTimeSecond: value ? item.fixedTimeSecond : null,
              }
            : item,
        ),
      }))
    },
    [onDraftChange],
  )

  const handleRemoveItem = React.useCallback(
    (key: string) => {
      onDraftChange((prev) => ({
        ...prev,
        items: prev.items.filter((item) => item.key !== key),
      }))
    },
    [onDraftChange],
  )

  const handleInsertItemAfter = React.useCallback(
    (targetIndex: number) => {
      const newItem = createDefaultDraftItem()
      onDraftChange((prev) => {
        const nextItems = prev.items.slice()
        if (nextItems.length === 0) {
          return { ...prev, items: [newItem] }
        }
        const clampedIndex = Math.min(Math.max(targetIndex, 0), nextItems.length - 1)
        nextItems.splice(clampedIndex + 1, 0, newItem)
        return { ...prev, items: nextItems }
      })
      setPendingFocusKey(newItem.key)
    },
    [createDefaultDraftItem, onDraftChange],
  )

  const handleAddItem = React.useCallback(() => {
    const newItem = createDefaultDraftItem()
    onDraftChange((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }))
  }, [createDefaultDraftItem, onDraftChange])

  const handlePendingFocusHandled = React.useCallback(
    (key: string) => {
      setPendingFocusKey((prev) => (prev === key ? null : prev))
    },
    [setPendingFocusKey],
  )

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      keepMounted
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: {
            xs: '100%',
            sm: `calc(100vw - ${navWidth}px)`,
          },
        },
      }}
    >
      <Box component="form" onSubmit={(event) => event.preventDefault()} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 3, py: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {headerTitle}
          </Typography>
          <IconButton aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider />
        {loading ? (
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : draft ? (
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, pb: (theme) => theme.spacing(8) }}>
            <Stack spacing={3}>
              <TextField
                label="Template name"
                value={draft.name}
                onChange={(event) => onDraftChange((prev) => ({ ...prev, name: event.target.value }))}
                fullWidth
                error={Boolean(errors.name)}
                helperText={errors.name ?? 'Give this running order a descriptive name.'}
              />
              <TextField
                label="Template notes"
                value={draft.notes}
                onChange={(event) => onDraftChange((prev) => ({ ...prev, notes: event.target.value }))}
                fullWidth
                multiline
                minRows={3}
                helperText="Optional notes for producers and presenters."
              />
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Items
                  </Typography>
                  <Chip label={`${draft.items.length}`} size="small" />
                  <Box sx={{ flexGrow: 1 }} />
                </Stack>
                {errors.items ? (
                  <Alert severity="error">{errors.items}</Alert>
                ) : null}
                <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                  <SortableContext items={draft.items.map((item) => item.key)} strategy={verticalListSortingStrategy}>
                    <Stack spacing={2}>
                      {(() => {
                        let cumulativePlannedSeconds = 0
                        return draft.items.map((item, index) => {
                          const displayTitle = displayTitles[item.key] ?? ''
                          const expectedStartSeconds = cumulativePlannedSeconds
                          const expectedStartLabel = formatExpectedStartLabel(expectedStartSeconds)
                          const itemDurationSeconds = Number.isFinite(item.durationSeconds)
                            ? Math.max(0, Math.floor(item.durationSeconds))
                            : 0
                          cumulativePlannedSeconds += itemDurationSeconds

                          return (
                            <SortableTemplateItemRow
                              key={item.key}
                              index={index}
                              item={item}
                              displayTitle={displayTitle}
                              expectedStartLabel={expectedStartLabel}
                              itemTypes={itemTypes}
                              errors={errors.itemErrors[item.key]}
                              onChange={handleChangeItem}
                              onToggleFixed={handleToggleFixed}
                              onRemove={handleRemoveItem}
                              onInsertAfter={handleInsertItemAfter}
                              pendingFocusKey={pendingFocusKey}
                              onPendingFocusHandled={handlePendingFocusHandled}
                              disableRemove={draft.items.length <= 1}
                              notesExpanded={expandedNotesByKey[item.key] ?? false}
                              onToggleNotes={handleToggleItemNotes}
                            />
                          )
                        })
                      })()}
                    </Stack>
                  </SortableContext>
                </DndContext>
                <Box sx={{ mt: 2, display: 'flex' }}>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddItem}>
                    Add item
                  </Button>
                </Box>
              </Stack>
              {errors.general ? <Alert severity="error">{errors.general}</Alert> : null}
            </Stack>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <Typography color="text.secondary">Select a template to begin editing.</Typography>
          </Box>
        )}
        <Divider />
        <Stack direction="row" spacing={2} sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button variant="outlined" onClick={onClose} fullWidth disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            fullWidth
            disabled={saving || !draft}
          >
            {saving ? 'Saving…' : mode === 'create' ? 'Create template' : 'Save changes'}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  )
}

const ShowClockTemplatesPage: React.FC = () => {
  const { me } = useOutletContext<OutletContext>()
  usePageTitle('Show Clock Templates', { showBreadcrumbs: false })

  const stations = me.stations ?? []
  const stationId = stations[0]?.id ?? null

  const [clockMeta, setClockMeta] = React.useState<ClockMetaResponse | null>(null)
  const itemTypes = React.useMemo(() => resolveClockItemTypes(clockMeta), [clockMeta])
  const stationTimezone = useStationStore((state) => state.activeStation?.timezone)
  const [list, setList] = React.useState<ClockTemplateSummary[]>([])
  const [page, setPage] = React.useState(1)
  const [perPage] = React.useState(10)
  const [total, setTotal] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [listError, setListError] = React.useState<string | null>(null)
  const [searchInput, setSearchInput] = React.useState('')
  const [searchQuery, setSearchQuery] = React.useState('')
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const [editorMode, setEditorMode] = React.useState<TemplateEditorMode>('create')
  const [draft, setDraft] = React.useState<TemplateDraft | null>(null)
  const [editorErrors, setEditorErrors] = React.useState<TemplateFormErrors>({ itemErrors: {} })
  const [editorLoading, setEditorLoading] = React.useState(false)
  const [editorSaving, setEditorSaving] = React.useState(false)
  const [snackbar, setSnackbar] = React.useState<SnackbarState | null>(null)
  const [deleteTarget, setDeleteTarget] = React.useState<ClockTemplateSummary | null>(null)
  const [deleteLoading, setDeleteLoading] = React.useState(false)

  React.useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(total / perPage))
    if (page > maxPage) {
      setPage(maxPage)
    }
  }, [page, perPage, total])

  React.useEffect(() => {
    let cancelled = false
    const controller = new AbortController()

    const loadMeta = async () => {
      if (!stationId || stationId <= 0) {
        return
      }
      try {
        const response = await getClockMeta({ stationId, signal: controller.signal })
        if (!cancelled) {
          setClockMeta(response)
        }
      } catch (error) {
        if (!cancelled) {
          setClockMeta(null)
        }
      }
    }

    loadMeta()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [stationId])

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearchQuery(searchInput.trim())
      setPage(1)
    }, 300)

    return () => window.clearTimeout(handle)
  }, [searchInput])

  React.useEffect(() => {
    if (!stationId) {
      return
    }

    let cancelled = false
    const controller = new AbortController()

    const loadTemplates = async () => {
      setLoading(true)
      setListError(null)
      try {
        const response = await listClockTemplates(
          {
            stationId,
            page,
            perPage,
            search: searchQuery || undefined,
          },
          { signal: controller.signal },
        )
        if (cancelled) {
          return
        }
        setList(response.templates)
        setTotal(response.total ?? response.templates.length)
      } catch (error) {
        if (cancelled) {
          return
        }
        const message =
          error instanceof Error && error.message
            ? error.message
            : 'Unable to load templates. Please try again.'
        setListError(message)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadTemplates()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [stationId, page, perPage, searchQuery])

  const handleOpenCreate = React.useCallback(() => {
    setEditorMode('create')
    setEditorErrors({ itemErrors: {} })
    setDraft(buildEmptyDraft(itemTypes))
    setDrawerOpen(true)
  }, [itemTypes])

  const loadTemplateDetail = React.useCallback(
    async (templateId: number) => {
      if (!stationId) {
        return
      }
      setEditorLoading(true)
      setEditorErrors({ itemErrors: {} })
      try {
        const template = await getClockTemplate(stationId, templateId)
        setDraft(buildDraftFromTemplate(template))
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : 'Unable to load template. Please try again.'
        setEditorErrors((prev) => ({ ...prev, general: message }))
      } finally {
        setEditorLoading(false)
      }
    },
    [stationId],
  )

  const handleEditTemplate = React.useCallback(
    (template: ClockTemplateSummary) => {
      setEditorMode('edit')
      setDrawerOpen(true)
      setDraft(null)
      loadTemplateDetail(template.id)
    },
    [loadTemplateDetail],
  )

  const validateDraft = React.useCallback((current: TemplateDraft | null): TemplateFormErrors => {
    const errors: TemplateFormErrors = { itemErrors: {} }

    if (!current) {
      errors.general = 'Template data is missing.'
      return errors
    }

    if (!current.name.trim()) {
      errors.name = 'Enter a template name.'
    }

    if (!current.items.length) {
      errors.items = 'Add at least one item to the template.'
    }

    current.items.forEach((item) => {
      const itemError: TemplateItemErrors = {}
      if (!item.type) {
        itemError.type = 'Choose a type.'
      }
      if (item.durationSeconds <= 0) {
        itemError.duration = 'Enter a positive duration.'
      }
      if (item.isFixedTime) {
        const normalizedTime = formatFixedTimeAnchorForPayload(item.fixedTimeMinute, item.fixedTimeSecond)
        if (!normalizedTime) {
          itemError.fixedTime = 'Select a start time.'
        }
      }
      if (Object.keys(itemError).length > 0) {
        errors.itemErrors[item.key] = itemError
      }
    })

    return errors
  }, [])

  const hasErrors = (errorState: TemplateFormErrors): boolean => {
    if (errorState.name || errorState.items || errorState.general) {
      return true
    }
    return Object.keys(errorState.itemErrors).length > 0
  }

  const handleSubmitDraft = React.useCallback(async () => {
    if (!stationId || !draft) {
      return
    }

    const validation = validateDraft(draft)
    setEditorErrors(validation)
    if (hasErrors(validation)) {
      return
    }

    const payload: ClockTemplateItemInput[] = draft.items.map((item, index) => normalizeDraftItem(item, index + 1))

    const templatePayload = {
      station_id: stationId,
      name: draft.name.trim(),
      notes: draft.notes.trim() ? draft.notes.trim() : null,
      items: payload,
    }

    const optimisticSummaryId = draft.id ?? createTempTemplateId()

    const optimisticSummary: ClockTemplateSummary = {
      id: optimisticSummaryId,
      station_id: stationId,
      name: draft.name.trim(),
      notes: templatePayload.notes,
      item_count: payload.length,
      estimated_duration_seconds: payload.reduce((total, item) => total + item.default_duration_seconds, 0),
      last_used_at: null,
      updated_at: new Date().toISOString(),
      created_at: draft.id ? undefined : new Date().toISOString(),
    }

    const previousList = list
    setEditorSaving(true)

    setList((prev) => {
      if (draft.id) {
        return prev.map((entry) => (entry.id === draft.id ? { ...entry, ...optimisticSummary } : entry))
      }
      return [optimisticSummary, ...prev]
    })
    if (!draft.id) {
      setTotal((prevTotal) => prevTotal + 1)
    }

    try {
      const response = draft.id
        ? await updateClockTemplate(draft.id, templatePayload)
        : await createClockTemplate(templatePayload)
      const savedSummary = templateDetailToSummary(response.template)
      if (draft.id) {
        setList((prev) => prev.map((entry) => (entry.id === savedSummary.id ? savedSummary : entry)))
      } else {
        setList((prev) => {
          const filtered = prev.filter((entry) => entry.id !== optimisticSummaryId && entry.id !== savedSummary.id)
          return [savedSummary, ...filtered]
        })
      }
      setSnackbar({ message: draft.id ? 'Template updated.' : 'Template created.', severity: 'success' })
      setDrawerOpen(false)
    } catch (error) {
      setList(previousList)
      if (!draft.id) {
        setTotal((prevTotal) => Math.max(0, prevTotal - 1))
      }
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Unable to save template. Please try again.'
      setEditorErrors((prev) => ({ ...prev, general: message }))
      setSnackbar({ message, severity: 'error' })
    } finally {
      setEditorSaving(false)
    }
  }, [draft, list, stationId, validateDraft])

  const handleDuplicate = React.useCallback(
    async (template: ClockTemplateSummary) => {
      if (!stationId) {
        return
      }
      const previousList = list
      try {
        const response = await duplicateClockTemplate(stationId, template.id)
        const summary = templateDetailToSummary(response.template)
        setList((prev) => [summary, ...prev])
        setTotal((prevTotal) => prevTotal + 1)
        setSnackbar({ message: 'Template duplicated.', severity: 'success' })
      } catch (error) {
        setList(previousList)
        const message =
          error instanceof Error && error.message
            ? error.message
            : 'Unable to duplicate template. Please try again.'
        setSnackbar({ message, severity: 'error' })
      }
    },
    [list, stationId],
  )

  const handleConfirmDelete = React.useCallback(async () => {
    if (!stationId || !deleteTarget) {
      return
    }
    const target = deleteTarget
    setDeleteLoading(true)
    const previousList = list
    setList((prev) => prev.filter((entry) => entry.id !== target.id))
    setTotal((prevTotal) => Math.max(0, prevTotal - 1))
    try {
      await deleteClockTemplate(stationId, target.id)
      setSnackbar({ message: 'Template deleted.', severity: 'success' })
    } catch (error) {
      setList(previousList)
      setTotal((prevTotal) => prevTotal + 1)
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Unable to delete template. Please try again.'
      setSnackbar({ message, severity: 'error' })
    } finally {
      setDeleteLoading(false)
      setDeleteTarget(null)
    }
  }, [deleteTarget, list, stationId])

  const templateCountLabel = React.useMemo(() => {
    if (total === 0) {
      return 'No templates yet'
    }
    if (total === 1) {
      return '1 template'
    }
    return `${total} templates`
  }, [total])

  if (!stationId) {
    return (
      <TypeChipProvider meta={clockMeta}>
        <Box sx={{ p: 4 }}>
          <Alert severity="info">You need access to at least one station to manage show clocks.</Alert>
        </Box>
      </TypeChipProvider>
    )
  }

  const pageCount = Math.max(1, Math.ceil(total / perPage))

  return (
    <TypeChipProvider meta={clockMeta}>
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Templates
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            placeholder="Search templates"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            New template
          </Button>
        </Stack>
      </Stack>
      <Paper sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {templateCountLabel}
            </Typography>
            <Chip icon={<AccessTimeIcon fontSize="small" />} label="Show Clock" size="small" />
            <Box sx={{ flexGrow: 1 }} />
          </Stack>
          {loading ? <LinearProgress sx={{ borderRadius: 999 }} /> : null}
          {listError ? <Alert severity="error">{listError}</Alert> : null}
          {!loading && !listError && list.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Build your first running order
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
                Create reusable templates for each show. Add blocks, set default durations, and pin fixed-time anchors.
              </Typography>
            </Box>
          ) : null}
          <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {list.map((template) => {
              const estimatedDuration = template.estimated_duration_seconds ?? null
              const lastUsed = formatClockRelativeDate(template.last_used_at, stationTimezone)
              const updated = formatClockRelativeDate(template.updated_at, stationTimezone)
              return (
                <Paper key={template.id} sx={{ borderRadius: 3, p: 2 }} variant="outlined">
                  <ListItem alignItems="flex-start" disableGutters>
                    <ListItemAvatar>
                      <Avatar variant="rounded" sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        {template.name.slice(0, 2).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {template.name}
                          </Typography>
                          <Chip label={`${template.item_count} item${template.item_count === 1 ? '' : 's'}`} size="small" />
                          {Number.isFinite(estimatedDuration) && estimatedDuration !== null ? (
                            <Chip label={`≈ ${formatClockDuration(estimatedDuration)}`} size="small" />
                          ) : null}
                        </Stack>
                      }
                      secondary={
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Last used: {lastUsed}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Updated: {updated}
                          </Typography>
                          {template.notes ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {template.notes}
                            </Typography>
                          ) : null}
                        </Stack>
                      }
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ ml: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditTemplate(template)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => handleDuplicate(template)}
                      >
                        Duplicate
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteTarget(template)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </ListItem>
                </Paper>
              )
            })}
          </List>
          {total > perPage ? (
            <Stack direction="row" justifyContent="center">
              <Pagination
                page={page}
                count={pageCount}
                onChange={(_event, value) => setPage(value)}
                color="primary"
              />
            </Stack>
          ) : null}
        </Stack>
      </Paper>

      <TemplateEditorDrawer
        open={drawerOpen}
        mode={editorMode}
        draft={draft}
        itemTypes={itemTypes}
        errors={editorErrors}
        saving={editorSaving}
        loading={editorLoading}
        onClose={() => {
          if (!editorSaving) {
            setDrawerOpen(false)
          }
        }}
        onDraftChange={(updater) => {
          setDraft((prev) => {
            const base = prev ?? buildEmptyDraft(itemTypes)
            return updater(base)
          })
          setEditorErrors((prev) => ({ ...prev, name: undefined, items: undefined, general: undefined, itemErrors: {} }))
        }}
        onSubmit={handleSubmitDraft}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={() => (!deleteLoading ? setDeleteTarget(null) : undefined)}>
        <DialogTitle>Delete template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the “{deleteTarget?.name}” template for your station. Daily clocks already created from this template
            will not be affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button color="error" onClick={handleConfirmDelete} disabled={deleteLoading}>
            {deleteLoading ? 'Deleting…' : 'Delete template'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
      </Box>
    </TypeChipProvider>
  )
}

export default ShowClockTemplatesPage
